import React, { useState, useEffect, useCallback } from 'react';
import { XMarkIcon, PhoneIcon, EnvelopeIcon, MapPinIcon, CalendarIcon, ClockIcon, PlusIcon } from '@heroicons/react/24/outline';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

const LeadDetailsModal = ({ lead, onClose, onUpdate }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [callHistory, setCallHistory] = useState([]);
  const [newCallNote, setNewCallNote] = useState('');
  const [callOutcome, setCallOutcome] = useState('');

  const loadNotes = useCallback(async () => {
    try {
      // For now, we'll use the notes field from the lead
      // In a more advanced setup, you might have a separate notes table
      if (lead?.notes) {
        // Split notes by timestamp if they contain multiple entries
        const noteEntries = lead.notes.split('\n\n').filter(entry => entry.trim());
        const formattedNotes = noteEntries.map((entry, index) => {
          const [timestamp, ...contentParts] = entry.split(': ');
          return {
            id: index + 1,
            content: contentParts.join(': '),
            created_at: timestamp || lead.updated_at || lead.created_at,
            type: 'general'
          };
        });
        setNotes(formattedNotes);
      } else {
        setNotes([]);
      }
    } catch (error) {
      console.error('Error loading notes:', error);
    }
  }, [lead]);

  const loadCallHistory = useCallback(async () => {
    try {
      // Create call history from lead's action data
      const history = [];
      
      if (lead.last_action && lead.last_action_date) {
        history.push({
          id: 1,
          action: lead.last_action,
          date: lead.last_action_date,
          outcome: lead.last_action,
          notes: `Last action: ${lead.last_action}`
        });
      }
      
      if (lead.follow_up_date) {
        history.push({
          id: 2,
          action: 'Follow-up Scheduled',
          date: lead.follow_up_date,
          outcome: 'Scheduled',
          notes: `Follow-up scheduled for ${new Date(lead.follow_up_date).toLocaleDateString()}`
        });
      }
      
      setCallHistory(history);
    } catch (error) {
      console.error('Error loading call history:', error);
    }
  }, [lead]);

  useEffect(() => {
    if (lead) {
      loadNotes();
      loadCallHistory();
    }
  }, [lead, loadNotes, loadCallHistory]);

  const addNote = async () => {
    if (!newNote.trim()) return;
    
    try {
      setLoading(true);
      
      // Update the lead's notes field
      const updatedNotes = lead.notes ? `${lead.notes}\n\n${new Date().toLocaleString()}: ${newNote}` : `${new Date().toLocaleString()}: ${newNote}`;
      
      const { error } = await supabase
        .from('leads')
        .update({ notes: updatedNotes })
        .eq('id', lead.id);
      
      if (error) throw error;
      
      toast.success('Note added successfully');
      setNewNote('');
      loadNotes();
      onUpdate && onUpdate();
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error(`Error adding note: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const addCallRecord = async () => {
    if (!newCallNote.trim() || !callOutcome) return;
    
    try {
      setLoading(true);
      
      // Update the lead with new call information
      const callRecord = `${new Date().toLocaleString()}: ${callOutcome} - ${newCallNote}`;
      const updatedNotes = lead.notes ? `${lead.notes}\n\n${callRecord}` : callRecord;
      
      const { error } = await supabase
        .from('leads')
        .update({ 
          notes: updatedNotes,
          last_action: callOutcome,
          last_action_date: new Date().toISOString()
        })
        .eq('id', lead.id);
      
      if (error) throw error;
      
      toast.success('Call record added successfully');
      setNewCallNote('');
      setCallOutcome('');
      loadNotes();
      loadCallHistory();
      onUpdate && onUpdate();
    } catch (error) {
      console.error('Error adding call record:', error);
      toast.error(`Error adding call record: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'follow_up': return 'bg-yellow-100 text-yellow-800';
      case 'converted': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="text-2xl font-bold text-gray-900">
                {lead?.name || 'Unknown Lead'}
              </h3>
              <p className="text-gray-600">Lead Details & History</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-8 w-8" />
            </button>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex space-x-8">
              {[
                { id: 'details', name: 'Details', icon: MapPinIcon },
                { id: 'notes', name: 'Notes', icon: EnvelopeIcon },
                { id: 'calls', name: 'Call History', icon: PhoneIcon }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
                >
                  <tab.icon className="h-4 w-4" />
                  <span>{tab.name}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="min-h-96">
            {activeTab === 'details' && (
              <div className="space-y-6">
                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-gray-900">Contact Information</h4>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3">
                        <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Email</p>
                          <p className="text-sm text-gray-600">{lead?.email || 'Not provided'}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <PhoneIcon className="h-5 w-5 text-gray-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Phone</p>
                          <p className="text-sm text-gray-600">{lead?.phone || 'Not provided'}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <MapPinIcon className="h-5 w-5 text-gray-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Company</p>
                          <p className="text-sm text-gray-600">{lead?.company || 'Not provided'}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-gray-900">Lead Information</h4>
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Source</p>
                        <p className="text-sm text-gray-600">{lead?.source || 'Not specified'}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">Status</p>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(lead?.status)}`}>
                          {lead?.status?.replace('_', ' ').toUpperCase() || 'NEW'}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">Priority</p>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(lead?.priority)}`}>
                          {lead?.priority?.toUpperCase() || 'MEDIUM'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Timeline */}
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-gray-900">Timeline</h4>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <CalendarIcon className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Created</p>
                        <p className="text-sm text-gray-600">{formatDate(lead?.created_at)}</p>
                      </div>
                    </div>
                    {lead?.last_action_date && (
                      <div className="flex items-center space-x-3">
                        <ClockIcon className="h-5 w-5 text-gray-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Last Action</p>
                          <p className="text-sm text-gray-600">
                            {lead.last_action} on {formatDate(lead.last_action_date)}
                          </p>
                        </div>
                      </div>
                    )}
                    {lead?.follow_up_date && (
                      <div className="flex items-center space-x-3">
                        <ClockIcon className="h-5 w-5 text-yellow-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Follow-up Due</p>
                          <p className="text-sm text-gray-600">{formatDate(lead.follow_up_date)}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'notes' && (
              <div className="space-y-6">
                {/* Add Note */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Add Note</h4>
                  <div className="space-y-3">
                    <textarea
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      placeholder="Add a note about this lead..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      rows={3}
                    />
                    <button
                      onClick={addNote}
                      disabled={loading || !newNote.trim()}
                      className="btn-primary flex items-center space-x-2"
                    >
                      <PlusIcon className="h-4 w-4" />
                      <span>Add Note</span>
                    </button>
                  </div>
                </div>

                {/* Notes List */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Notes History</h4>
                  {notes.length > 0 ? (
                    <div className="space-y-3">
                      {notes.map((note, index) => (
                        <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
                          <div className="flex justify-between items-start mb-2">
                            <span className="text-sm text-gray-500">{formatDate(note.created_at)}</span>
                          </div>
                          <p className="text-gray-900 whitespace-pre-wrap">{note.content}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">No notes yet. Add your first note above.</p>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'calls' && (
              <div className="space-y-6">
                {/* Add Call Record */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Record Call</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Call Outcome</label>
                      <select
                        value={callOutcome}
                        onChange={(e) => setCallOutcome(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">Select outcome</option>
                        <option value="Connected">Connected</option>
                        <option value="No Answer">No Answer</option>
                        <option value="Wrong Number">Wrong Number</option>
                        <option value="Do Not Call">Do Not Call</option>
                        <option value="Interested">Interested</option>
                        <option value="Not Interested">Not Interested</option>
                        <option value="Already Customer">Already Customer</option>
                      </select>
                    </div>
                    <textarea
                      value={newCallNote}
                      onChange={(e) => setNewCallNote(e.target.value)}
                      placeholder="Add call notes..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      rows={3}
                    />
                    <button
                      onClick={addCallRecord}
                      disabled={loading || !newCallNote.trim() || !callOutcome}
                      className="btn-primary flex items-center space-x-2"
                    >
                      <PhoneIcon className="h-4 w-4" />
                      <span>Record Call</span>
                    </button>
                  </div>
                </div>

                {/* Call History */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Call History</h4>
                  {callHistory.length > 0 ? (
                    <div className="space-y-3">
                      {callHistory.map((call, index) => (
                        <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
                          <div className="flex justify-between items-start mb-2">
                            <span className="font-medium text-gray-900">{call.action}</span>
                            <span className="text-sm text-gray-500">{formatDate(call.date)}</span>
                          </div>
                          <p className="text-gray-600">{call.notes}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">No call history yet. Record your first call above.</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadDetailsModal;
